import { useContext, useEffect, useMemo, useState } from 'react'
import { Box, Button, Chip, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { EditOutlined } from '@mui/icons-material'
import EmissionCalculationService from '../../../../shared/services/emission-calculation/emission-calculation.service'
import { IEmissionCalculation, provisionalFactorLabel, estimatedFactorLabel } from '../../../../shared/services/emission-calculation/emission-calculation.model'
import { IConversion } from '../../../../shared/services/conversion/conversion.model'
import { ShallowFormDisplay } from '../../form-field-components/general/shallow-form-display'
import moment from 'moment'
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels'
import { BackButtonLink } from '../../display-elements-components/back-button-link'
import { useFlagEmissionsCapability } from '../../../../hooks/use-flag-emissions-capability'
import { formatFlagCategoryDisplay } from '../../../../shared/constants/flag-emissions'
import { ELECTRICITY_CATEGORY_ID, FUELS_CATEGORY_ID } from '../../../../shared/constants/category-ids'

interface EmissionCalculationProps {
  ids: string[]
  onBack?: () => void
  canEdit?: boolean
  onEdit?: (calculation: IEmissionCalculation) => void
}

const formatNumber = (value: number, maxDecimals = 2) =>
  new Intl.NumberFormat('en-GB', { maximumFractionDigits: maxDecimals }).format(value)

const formatEmissionFactorDetail = (conversion: IConversion | null | undefined): string | null => {
  if (!conversion) return null
  const parts = [
    conversion.detailLevelOne,
    conversion.detailLevelTwo,
    conversion.detailLevelThree,
    conversion.detailLevelFour,
    conversion.detailLevelFive,
  ].filter((p): p is string => typeof p === 'string' && p.trim() !== '')
  return parts.length > 0 ? parts.join(' > ') : null
}

function coerceBool(value: unknown): boolean {
  if (value === true || value === 1) return true
  if (value === false || value === 0) return false
  if (value === null || value === undefined) return false
  if (typeof value === 'string') {
    const s = value.toLowerCase().trim()
    if (s === 'true' || s === '1') return true
    if (s === 'false' || s === '0' || s === '') return false
  }
  return Boolean(value)
}

const getCalculationDisplayPriority = (calculation: IEmissionCalculation): number => {
  if (calculation.type === 'ACTIVITY' && coerceBool(calculation.isWtt)) return 3
  if (calculation.type === 'ACTIVITY' && coerceBool(calculation.isMarketBased)) return 2
  if (calculation.type === 'ACTIVITY') return 1
  return 0
}

const getSelectableCalculations = (rows: IEmissionCalculation[]): IEmissionCalculation[] => {
  const sortedCalculations = [...rows]
    .filter((calculation) => calculation.type !== 'MEASUREMENT')
    .sort((a, b) => {
      if (a.calendarYear !== b.calendarYear) return a.calendarYear - b.calendarYear
      return getCalculationDisplayPriority(a) - getCalculationDisplayPriority(b)
    })

  const calculationsByYear = new Map<number, IEmissionCalculation>()
  sortedCalculations.forEach((calculation) => {
    if (!calculationsByYear.has(calculation.calendarYear)) {
      calculationsByYear.set(calculation.calendarYear, calculation)
    }
  })

  return [...calculationsByYear.values()]
}

export const EmissionCalculation = ({ ids, onBack, canEdit = false, onEdit }: EmissionCalculationProps) => {
  const emissionCalculationService = new EmissionCalculationService()
  const [calculations, setCalculations] = useState<IEmissionCalculation[]>([])
  const [selectedCalculation, setSelectedCalculation] = useState<IEmissionCalculation | null>(null)
  const screenLabelsContext = useContext(ScreenLabelsContext)
  const { flagEnabled } = useFlagEmissionsCapability()
  const selectableCalculations = useMemo(() => getSelectableCalculations(calculations), [calculations])


  useEffect(() => {
    const run = async () => {
      try {
        const results = await emissionCalculationService.getAll(ids)
        setCalculations(results.rows)
      } catch (error) {
        console.error(error)
      }
    }

    if (ids.length > 0) {
      run()
    }
  }, [ids])

  useEffect(() => {
    if (selectableCalculations.length === 0) {
      setSelectedCalculation(null)
      return
    }

    setSelectedCalculation((currentCalculation) => {
      if (!currentCalculation) return selectableCalculations[0]

      const refreshedCurrentCalculation = calculations.find((calculation) => calculation.id === currentCalculation.id)
      if (!refreshedCurrentCalculation) return selectableCalculations[0]

      if (selectableCalculations.some((calculation) => calculation.id === refreshedCurrentCalculation.id)) {
        return refreshedCurrentCalculation
      }

      return selectableCalculations.find((calculation) => calculation.calendarYear === refreshedCurrentCalculation.calendarYear)
        ?? selectableCalculations[0]
    })
  }, [calculations, selectableCalculations])

  const canEditSelectedCalculation = !!(
    canEdit &&
    selectedCalculation &&
    !(selectedCalculation.type === 'ACTIVITY' && coerceBool(selectedCalculation.isWtt))
  )

  return (
    <div>
      {(onBack || canEditSelectedCalculation) && (
        <Stack direction='row' justifyContent='space-between' alignItems='center' flexWrap='wrap' gap={1} sx={{ mb: 2 }}>
          <Box>
            {onBack && <BackButtonLink buttonText='Back to calculations' onClick={onBack} />}
          </Box>
          {canEditSelectedCalculation && selectedCalculation && (
            <Button
              variant='contained'
              startIcon={<EditOutlined fontSize='small' />}
              onClick={() => onEdit?.(selectedCalculation)}
            >
              Edit calculation
            </Button>
          )}
        </Stack>
      )}
      {selectableCalculations.length > 1 && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <ToggleButtonGroup
            value={selectedCalculation?.id}
            exclusive
            onChange={(_, value) => {
              if (value !== null) {
                setSelectedCalculation(selectableCalculations.find((calculation) => calculation.id === value) ?? null)
              }
            }}
          >
            {selectableCalculations.map((calc) => (
              <ToggleButton key={calc.id} value={calc.id} sx={{ textTransform: 'none', minWidth: '80px', py: 0.5 }}>
                {calc.calendarYear}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}

      {selectedCalculation && (
        <>
          {<ShallowFormDisplay small={true} isTopRow={true} name='Type of Entry' value={
            selectedCalculation.type === 'PRODUCT' ? 'Product Entry' :
              selectedCalculation.type === 'DIRECT' ? 'Direct Entry' :
                selectedCalculation.type === 'SPEND' ? 'Spend-based Entry' :
                  selectedCalculation.type === 'ACTIVITY' && selectedCalculation.isWtt ? 'Activity-based Entry - Well-to-tank' :
                    selectedCalculation.type === 'ACTIVITY' && selectedCalculation.isMarketBased ? 'Activity-based Entry - Market-based' :
                      selectedCalculation.type === 'ACTIVITY' && !selectedCalculation.isMarketBased && !selectedCalculation.isWtt && selectedCalculation.scope === '2' ? 'Activity-based Entry - Location-based' :
                        selectedCalculation.type === 'ACTIVITY' ? 'Activity-based Entry' :
                          selectedCalculation.type === 'MEASUREMENT' ? 'Measurement' :
                            'Unknown'
          } />}
          <ShallowFormDisplay small={true} name='Calendar Year' value={selectedCalculation.calendarYear} />
          <ShallowFormDisplay small={true} name='Start Date' value={moment(selectedCalculation.startDate).format('DD/MM/YYYY')} />
          <ShallowFormDisplay small={true} name='End Date' value={moment(selectedCalculation.endDate).format('DD/MM/YYYY')} />
          <ShallowFormDisplay small={true} name='Data Type' value={selectedCalculation.dataType.dataType}
            chip={{
              label: `Data Quality: ${selectedCalculation.dataType.dataQuality}`, color: selectedCalculation.dataType.dataQuality === 'High' ? 'success' :
                selectedCalculation.dataType.dataQuality === 'Medium' ? 'warning' :
                  'error'
            }} />
          {/* //this isnt finished, need to consider import, meter reading etc */}
          <ShallowFormDisplay small={true} name='Method of Input' value={selectedCalculation.thirdPartyConnectionImportLineId ? 'Integrated' :
            selectedCalculation.reportActionId ? 'Task' :
              selectedCalculation.relatedModelName === 'meterReading' ? 'Meter Reading' :
                selectedCalculation.relatedModelName === 'fileUploadLineSpendBased' || selectedCalculation.relatedModelName === 'fileUploadLine' || selectedCalculation.relatedModelName === 'fileUploadLineGoodEmission' || selectedCalculation.relatedModelName === 'fileUploadLineDirectEmission' ? 'Imported' :
                  'Manual'} />
          {/* TICKET-E: biogenic combustion is a Scope-1-type activity, but its CO2 is reported outside the
              scopes (not in the Scope 1 total). Clarify in the display; the stored scope is unchanged. */}
          <ShallowFormDisplay
            small={true}
            name='Scope'
            value={selectedCalculation.isBiogenic ? `${selectedCalculation.scope} (reported outside of scopes)` : selectedCalculation.scope}
          />
          <ShallowFormDisplay small={true} name='Group' value={selectedCalculation.group.title} />
          {selectedCalculation.category && !selectedCalculation.isWtt && <ShallowFormDisplay small={true} name='Category' value={selectedCalculation.category.friendlyName || selectedCalculation.category.name} />}
          {selectedCalculation.category && selectedCalculation.isWtt && <ShallowFormDisplay small={true} name='Category' value={selectedCalculation.category.friendlyName || selectedCalculation.category.name} />}
          {flagEnabled && (
            <>
              <ShallowFormDisplay
                small={true}
                name='FLAG classification'
                value={selectedCalculation.isFlag === true ? 'Yes' : 'No'}
              />
              {selectedCalculation.isFlag === true && (
                <>
                  <ShallowFormDisplay
                    small={true}
                    name='FLAG category'
                    value={formatFlagCategoryDisplay(selectedCalculation.flagCategory ?? undefined)}
                  />
                </>
              )}
            </>
          )}
          {/* Spend-Based Entry Specifics */}
          {selectedCalculation.type === 'SPEND' && selectedCalculation.spendBasedConversion && selectedCalculation.spendBasedConversion.activity && <ShallowFormDisplay small={true} name='Activity' value={`${selectedCalculation.spendBasedConversion.activity} | ${selectedCalculation.spendBasedConversion.localAuthorityCustomIdentifier}`} />}
          {/* Ledger origin (nominal code) for spend entries imported by nominal code */}
          {selectedCalculation.type === 'SPEND' && selectedCalculation.emission?.nominalCode && <ShallowFormDisplay small={true} name='Nominal Code' value={selectedCalculation.emission.nominalDescription ? `${selectedCalculation.emission.nominalCode} - ${selectedCalculation.emission.nominalDescription}` : selectedCalculation.emission.nominalCode} />}
          {selectedCalculation.type === 'SPEND' && (() => {
            const measurementCalc = calculations.find(c =>
              c.type === 'MEASUREMENT' &&
              c.calendarYear === selectedCalculation.calendarYear
            )
            if (measurementCalc) {
              const sameUnit = measurementCalc.measurementUnit.name === measurementCalc.outcomeUnitMeasurementUnit.name
              return (
                <>
                  <ShallowFormDisplay small={true} name='Units Consumed' value={`${formatNumber(measurementCalc.unitsConsumed)} ${measurementCalc.measurementUnit.name}`} />
                  {!sameUnit && (
                    <>
                      <ShallowFormDisplay small={true} name='Additional Calculation Multiplier' value={formatNumber(measurementCalc.multiplier, 6)} />
                      <ShallowFormDisplay small={true} name='Additional Calculation Value' value={`${formatNumber(measurementCalc.outcomeAmount)} ${measurementCalc.outcomeUnitMeasurementUnit.name}`} />
                    </>
                  )}
                </>
              )
            }
            return <ShallowFormDisplay small={true} name='Units Consumed' value={`${formatNumber(selectedCalculation.unitsConsumed)} ${selectedCalculation.measurementUnit.name}`} />
          })()}
          {selectedCalculation.type === 'SPEND' && (() => {
            // The conversion record lives on the spend entry's MEASUREMENT sibling for the same
            // year (the same lookup Original Spend uses below); a legacy row may carry its own.
            const measurementCalc = calculations.find(c => c.type === 'MEASUREMENT' && c.calendarYear === selectedCalculation.calendarYear)
            const cc = measurementCalc?.transactionCurrencyConversions?.[0] ?? selectedCalculation.transactionCurrencyConversions?.[0]
            if (!cc || cc.originalCurrencyCode === cc.convertedCurrencyCode) return null
            const isFixed = cc.exchangeRateBasis === 'PERIOD_FIXED'
            return (
              <ShallowFormDisplay small={true} name='Exchange Rate' value={
                <Stack direction='row' alignItems='center' flexWrap='wrap' gap={1}>
                  <Typography variant='body1' sx={{ color: 'text.secondary' }}>
                    1 {cc.originalCurrencyCode} = {formatNumber(cc.exchangeRate, 6)} {cc.convertedCurrencyCode}
                  </Typography>
                  {isFixed
                    ? <Chip label='Fixed for this reporting period' color='info' size='small' />
                    : <Chip label={cc.exchangeDate ? `Daily market rate on ${moment(cc.exchangeDate).format('DD/MM/YYYY')}` : 'Daily market rate'} size='small' />}
                  {isFixed && cc.exchangeRateSource && (
                    <Typography variant='body2' sx={{ color: 'text.secondary' }}>Source: {cc.exchangeRateSource}</Typography>
                  )}
                </Stack>
              } />
            )
          })()}
          {/* Spend factors are fractions of a kg per currency unit (DEFRA publishes 0.153 kgCO2e per
              GBP for IT services), so two decimals rounded the factor to 0.15 and the row's own
              arithmetic no longer reconciled with the emission below it. */}
          {selectedCalculation.type === 'SPEND' && <ShallowFormDisplay small={true} name='Conversion Factor' value={`${formatNumber(selectedCalculation.multiplier, 6)}`} />}
          {selectedCalculation.type === 'SPEND' && <ShallowFormDisplay small={true} name='Conversion Factor Source' value={`${selectedCalculation.spendBasedConversion.conversionSource.source} | ${selectedCalculation.spendBasedConversion.calendarYear}`} />}
          {selectedCalculation.type === 'SPEND' && (() => {
            const measurementCalc = calculations.find(c => c.type === 'MEASUREMENT' && c.calendarYear === selectedCalculation.calendarYear);
            const cc = measurementCalc?.transactionCurrencyConversions?.[0];
            if (!cc) return null;
            return (
              <>
                <ShallowFormDisplay small={true} name='Original Spend' value={`${formatNumber(cc.originalAmount)} ${cc.originalCurrencyCode}`} />
                {cc.inflationAdjustmentApplied && cc.inflatedAmount != null && (
                  <ShallowFormDisplay small={true} name='Inflation-adjusted Spend' value={`${formatNumber(cc.inflatedAmount)} ${cc.originalCurrencyCode}`} />
                )}
                <ShallowFormDisplay small={true} name='Inflation Adjustment' value={
                  cc.inflationAdjustmentApplied
                    ? (cc.inflationAdjustmentReason === 'BRIDGED'
                      ? <Chip label='Applied (closest CPI year)' color='warning' size='small' />
                      : <Chip label='Applied' color='success' size='small' />)
                    : (cc.inflationAdjustmentReason === 'CPI_MISSING'
                      // A missing CPI index is a data-quality gap, never a neutral no-op: the
                      // spend was used unadjusted because no index exists for the factor currency.
                      ? <Chip label={`Not applied (no CPI index for ${cc.convertedCurrencyCode || 'the factor currency'})`} color='warning' size='small' />
                      : cc.inflationAdjustmentReason === 'SAME_YEAR'
                        ? <Chip label='Not needed (spend year equals factor year)' size='small' />
                        : <Chip label='Not applied' size='small' />)
                } />
                {cc.inflationAdjustmentApplied && cc.inflationFactor != null && cc.inflationFactor !== 1 && (
                  <ShallowFormDisplay small={true} name='Adjustment Ratio' value={formatNumber(cc.inflationFactor, 6)} />
                )}
                {cc.inflationAdjustmentApplied && cc.inflationIndexName && (
                  <ShallowFormDisplay small={true} name='Inflation Index' value={cc.inflationIndexName} />
                )}
              </>
            );
          })()}
          {/* Product Entry Specifics */}
          {selectedCalculation.type === 'PRODUCT' && selectedCalculation.capitalGood && <ShallowFormDisplay small={true} name={screenLabelsContext.getLabel('product', { plural: false, capitalize: 'first' })} value={selectedCalculation.capitalGood.name} />}
          {selectedCalculation.type === 'PRODUCT' && selectedCalculation.capitalGood && <ShallowFormDisplay small={true} name='Model Number' value={selectedCalculation.capitalGood.modelNumber} />}
          {selectedCalculation.type === 'PRODUCT' && selectedCalculation.manufacturer && <ShallowFormDisplay small={true} name='Manufacturer' value={selectedCalculation.manufacturer.name} />}
          {selectedCalculation.type === 'PRODUCT' && selectedCalculation.goodCategory && <ShallowFormDisplay small={true} name='Product Category' value={[
            selectedCalculation.goodCategory.category,
            selectedCalculation.goodCategory.subCategoryOne,
            selectedCalculation.goodCategory.subCategoryTwo,
          ].filter(Boolean).join(' > ')} />}
          {selectedCalculation.type === 'PRODUCT' && <ShallowFormDisplay small={true} name='Emissions Per Unit' value={`${selectedCalculation.capitalGood.kgCarbonEmission} kg CO2e per ${selectedCalculation.measurementUnitAmount} ${selectedCalculation.measurementUnit.name}`} />}
          {selectedCalculation.type === 'PRODUCT' && <ShallowFormDisplay small={true} name='Source of Emission' value={selectedCalculation.capitalGood.sourceOfEmission} />}
          {selectedCalculation.type === 'PRODUCT' && <ShallowFormDisplay small={true} name='Number of Units Purchased' value={`${formatNumber(selectedCalculation.unitsConsumed)} of ${selectedCalculation.measurementUnitAmount} ${selectedCalculation.measurementUnit.name}`} />}
          {/* Activity Entry Specifics */}
          {selectedCalculation.type === 'ACTIVITY' && (() => {
            const sqftCalc = calculations.find(c =>
              c.type === 'MEASUREMENT' &&
              c.measurementUnit.name === 'square foot' &&
              c.calendarYear === selectedCalculation.calendarYear
            )
            const consumptionTypeCode =
              selectedCalculation.category?.id === ELECTRICITY_CATEGORY_ID ? 'ELECTRICITY' :
                selectedCalculation.category?.id === FUELS_CATEGORY_ID ? 'GAS' : null
            const m2Calc = consumptionTypeCode ? calculations.find(c =>
              c.type === 'MEASUREMENT' &&
              c.measurementUnit.name === 'm2' &&
              c.calendarYear === selectedCalculation.calendarYear &&
              c.squareFootConversion?.consumptionTypeCode === consumptionTypeCode
            ) : null
            if (sqftCalc) {
              return (
                <>
                  <ShallowFormDisplay small={true} name='Units Consumed' value={`${formatNumber(sqftCalc.unitsConsumed)} ${sqftCalc.measurementUnit.name} > ${formatNumber(sqftCalc.outcomeAmount)} ${sqftCalc.outcomeUnitMeasurementUnit.name}`} />
                  {m2Calc?.squareFootConversion && <>
                    <ShallowFormDisplay small={true} name='Additional Calculation Multiplier' value={formatNumber(m2Calc.squareFootConversion.multiplier, 6)} />
                    <ShallowFormDisplay small={true} name='Additional Calculation Source' value={`${m2Calc.squareFootConversion.conversionSource.source} | ${m2Calc.squareFootConversion.calendarYear}`} />
                    <ShallowFormDisplay
                      small={true}
                      name='Additional Calculation Value'
                      value={`${formatNumber(m2Calc.outcomeAmount)} ${m2Calc.outcomeUnitGreenHouseGasUnit.name}`}
                      chip={m2Calc.isProvisional ? { label: provisionalFactorLabel(m2Calc.provisionalReason), color: 'warning' } : undefined}
                    />
                  </>}
                </>
              )
            }
            return <ShallowFormDisplay small={true} name='Units Consumed' value={`${formatNumber(selectedCalculation.unitsConsumed)} ${selectedCalculation.measurementUnit.name}`} />
          })()}
          {selectedCalculation.type === 'ACTIVITY' && <ShallowFormDisplay small={true} name='Conversion Factor' value={`${formatNumber(selectedCalculation.multiplier, 4)}`} />}
          {/* Activity Entry Specifics - isMarketBased */}
          {selectedCalculation.type === 'ACTIVITY' && selectedCalculation.isMarketBased && <ShallowFormDisplay small={true} name='Conversion Factor Source' value={
            selectedCalculation.marketBasedSource === 'CUSTOM' ? 'Custom' :
              selectedCalculation.marketBasedSource === 'RESIDUAL_FUEL_MIX_ID' && selectedCalculation.residualFuelMix ? `${selectedCalculation.residualFuelMix.conversionSource.source}, Residual Fuel Mix | ${selectedCalculation.residualFuelMix.calendarYear}` :
                selectedCalculation.marketBasedSource === 'ENERGY_SUPPLIER_TARIFF_DETAIL_ID' && selectedCalculation.energySupplierTariffDetail ? `${selectedCalculation.energySupplierTariffDetail.energySupplierName} - ${selectedCalculation.energySupplierTariffDetail.energySupplierTariffName}` :
                  selectedCalculation.marketBasedSource === 'CONVERSION_ID' ? `${selectedCalculation.marketBasedConversion.conversionSource.source} | ${selectedCalculation.marketBasedConversion.calendarYear}` :
                    'Unknown'
          } />}
          {/* Activity Entry Specifics - isWtt/Location */}
          {selectedCalculation.type === 'ACTIVITY' && selectedCalculation.conversion && <ShallowFormDisplay small={true} name='Conversion Factor Source' value={`${selectedCalculation.conversion.conversionSource.source} | ${selectedCalculation.conversion.calendarYear}`} />}
          {selectedCalculation.type === 'ACTIVITY' && (() => {
            const locationDetail = formatEmissionFactorDetail(selectedCalculation.conversion)
            const marketDetail =
              selectedCalculation.isMarketBased && selectedCalculation.marketBasedSource === 'CONVERSION_ID'
                ? formatEmissionFactorDetail(selectedCalculation.marketBasedConversion)
                : null
            if (!locationDetail && !marketDetail) return null
            if (marketDetail && locationDetail && marketDetail === locationDetail) {
              return <ShallowFormDisplay small={true} name='Emission factor detail' value={marketDetail} />
            }
            return (
              <>
                {marketDetail && (
                  <ShallowFormDisplay
                    small={true}
                    name={locationDetail && marketDetail !== locationDetail ? 'Market-based emission factor detail' : 'Emission factor detail'}
                    value={marketDetail}
                  />
                )}
                {locationDetail && (!marketDetail || marketDetail !== locationDetail) && (
                  <ShallowFormDisplay
                    small={true}
                    name={marketDetail ? 'Location-based emission factor detail' : 'Emission factor detail'}
                    value={locationDetail}
                  />
                )}
              </>
            )
          })()}

          {selectedCalculation.transactionRenewableEnergyCertificates && selectedCalculation.transactionRenewableEnergyCertificates.length > 0 && (
            <ShallowFormDisplay
              small={true}
              // Gas (RGGO) certificates are only ever linked to scope-1 Company Facilities / Fuels
              // calculations and, unlike electricity certificates, do not reduce the emission amount.
              name={
                (selectedCalculation.groupId === '22cc8b45-aa77-49aa-8fb2-c39bb884fa13'
                  && selectedCalculation.categoryId === 'f88cd55e-9e30-4605-8106-24a53e0b5f62')
                  ? 'Renewable Gas Certificates (No reduction applied)'
                  : 'Renewable Energy Certificates'
              }
              value={
                <Stack>
                  {selectedCalculation.transactionRenewableEnergyCertificates.map(rec => {
                    // The link row carries the certificate nested under `renewableEnergyCertificate`;
                    // fall back to the top-level field for any already-flattened shapes.
                    const certificate = rec as unknown as {
                      id: string
                      certificateNumber?: string
                      energykwhUsed?: number
                      renewableEnergyCertificate?: { certificateNumber?: string }
                    }
                    const certificateNumber = certificate.renewableEnergyCertificate?.certificateNumber ?? certificate.certificateNumber
                    return (
                      <Typography key={certificate.id} variant='body1' sx={{ color: 'text.secondary' }}>
                        Certificate {certificateNumber} - {formatNumber(rec.energykwhUsed)} kWh
                      </Typography>
                    )
                  })}
                </Stack>
              }
            />
          )}

          <ShallowFormDisplay
            small={true}
            isBottomRow={
              !(selectedCalculation.transactionGreenHouseGasBreakdowns?.length > 0) &&
              !(selectedCalculation.transactionEnergyBreakdowns?.length > 0)
            }
            name='Total Emission'
            value={`${formatNumber(selectedCalculation.emissionAmount)} ${selectedCalculation.greenHouseGasUnit.name}`}
            chips={[
              ...(selectedCalculation.isProvisional ? [{ label: provisionalFactorLabel(selectedCalculation.provisionalReason), color: 'warning' as const }] : []),
              ...(selectedCalculation.isEstimated ? [{ label: estimatedFactorLabel(selectedCalculation.estimatedReason), color: 'warning' as const }] : []),
              // TICKET-E: biogenic combustion CO2 is excluded from the scope totals and reported separately.
              ...(selectedCalculation.isBiogenic ? [{ label: 'Biogenic (outside of scopes)', color: 'info' as const }] : []),
            ]}
          />
          {selectedCalculation.transactionGreenHouseGasBreakdowns && selectedCalculation.transactionGreenHouseGasBreakdowns.length > 0 && (
            <ShallowFormDisplay
              small={true}
              isBottomRow={!(selectedCalculation.transactionEnergyBreakdowns?.length > 0)}
              name='Emission Breakdown'
              value={
                <Stack>
                  {selectedCalculation.transactionGreenHouseGasBreakdowns.map(item => (
                    <Typography key={item.greenHouseGasUnit.name} variant='body1' sx={{ color: 'text.secondary' }}>
                      {formatNumber(item.emissionAmount)} {item.greenHouseGasUnit.name}
                    </Typography>
                  ))}
                </Stack>
              }
            />
          )}
          {selectedCalculation.transactionEnergyBreakdowns && selectedCalculation.transactionEnergyBreakdowns.length > 0 && (
            <>
              {selectedCalculation.transactionEnergyBreakdowns.map((item, index) => {
                const renewablePercent = item.kwhAmount > 0
                  ? Math.round(((item.renewableKwh || 0) / item.kwhAmount) * 100)
                  : 0
                const energyChips = [
                  ...(selectedCalculation.isProvisional ? [{ label: provisionalFactorLabel(selectedCalculation.provisionalReason), color: 'warning' as const }] : []),
                  ...(selectedCalculation.isEstimated ? [{ label: estimatedFactorLabel(selectedCalculation.estimatedReason), color: 'warning' as const }] : []),
                  ...((item.renewableKwh || 0) > 0 ? [{ label: `${renewablePercent}% Renewable Energy`, color: 'success' as const }] : []),
                ]
                const isSpecialSource = item.conversion?.conversionSourceId === '5ff32008-8aba-4329-af63-99c733c90494'
                const isLastItem = index === selectedCalculation.transactionEnergyBreakdowns.length - 1
                const breakdownFactorDetail = formatEmissionFactorDetail(item.conversion)

                if (isSpecialSource && item.conversion.energyContentFactor) {
                  return (
                    <Box key={item.energyUnit.name}>
                      <ShallowFormDisplay small={true} name='Energy Content Factor' value={formatNumber(item.conversion.energyContentFactor, 4)} />
                      <ShallowFormDisplay small={true} name='Energy Content Conversion Units' value={`${selectedCalculation.measurementUnit.name} > GJ`} />
                      <ShallowFormDisplay small={true} name='Energy Conversion Factor' value='277.78' />
                      <ShallowFormDisplay small={true} name='Energy Content Conversion Units' value='GJ > kWh' />
                      <ShallowFormDisplay small={true} name='Energy Consumption' value={`${formatNumber(item.kwhAmount)} ${item.energyUnit.name}`} chips={energyChips} />
                      <ShallowFormDisplay small={true} name='Energy Conversion Factor Source' value={`${item.conversion.conversionSource.source} | ${item.conversion.calendarYear}`} />
                      {breakdownFactorDetail && (
                        <ShallowFormDisplay small={true} name='Emission factor detail' value={breakdownFactorDetail} />
                      )}
                      <ShallowFormDisplay small={true} name='GHG Conversion Factor' value={formatNumber(selectedCalculation.multiplier, 4)} />
                      <ShallowFormDisplay small={true} isBottomRow={isLastItem} name='GHG Conversion Units' value={`GJ > ${selectedCalculation.greenHouseGasUnit.name}`} />
                    </Box>
                  )
                }

                if (isSpecialSource && item.conversion.volumeMassConverter) {
                  return (
                    <Box key={item.energyUnit.name}>
                      <ShallowFormDisplay small={true} name='Volume Mass Converter' value={formatNumber(item.conversion.volumeMassConverter, 4)} />
                      <ShallowFormDisplay small={true} name='Volume Mass Conversion Units' value={`${selectedCalculation.measurementUnit.name} > tonnes`} />
                      {breakdownFactorDetail && (
                        <ShallowFormDisplay small={true} name='Emission factor detail' value={breakdownFactorDetail} />
                      )}
                      <ShallowFormDisplay small={true} name='GHG Conversion Factor' value={formatNumber(selectedCalculation.multiplier, 4)} />
                      <ShallowFormDisplay small={true} isBottomRow={isLastItem} name='GHG Conversion Units' value={`tonnes > ${selectedCalculation.greenHouseGasUnit.name}`} />
                    </Box>
                  )
                }

                return (
                  <Box key={item.energyUnit.name}>
                    {item.conversion ? (
                      <>
                        <ShallowFormDisplay small={true} name='Conversion Factor' value={formatNumber(item.conversion.energyContentFactor, 4)} />
                        <ShallowFormDisplay small={true} name='Conversion Factor Source' value={`${item.conversion.conversionSource.source} | ${item.conversion.calendarYear}`} />
                        {breakdownFactorDetail && (
                          <ShallowFormDisplay small={true} name='Emission factor detail' value={breakdownFactorDetail} />
                        )}
                      </>
                    ) : null}
                    <ShallowFormDisplay
                      small={true}
                      isBottomRow={isLastItem}
                      name='Energy Consumption'
                      value={`${formatNumber(item.kwhAmount)} ${item.energyUnit.name}`}
                      chips={energyChips}
                    />
                  </Box>
                )
              })}
            </>
          )}

        </>
      )}
    </div>
  )
}
